/* global indexedDB, Sentry, IDBKeyRange */
import * as vibes from './vibeArray'
import algoliasearch from 'algoliasearch'
import * as urlHelpers from '../urlHelpers'
import { getLocaleFromPath } from '../localeHelpers'
import { haveGtmAndGaLoaded } from '../../analytics/AnalyticsHelpers'
import { t } from './translations'
import { typesenseEnabled, typesenseSearchIndex, primaryIndexName, adapterOverrides, rankingField } from '../../libs/search/searchClient'
import { escapeValue } from './filterSyntax'

function stringParameterize (str) {
  return str.trim().toLowerCase().replace(/[^a-zA-Z0-9 -]/, '').replace(/\s/g, '-')
}

export function getTable (dataId) {
  const element = document.querySelector(`[data-id="content-${dataId}"]`)
  return (element !== null) ? JSON.parse(element.innerHTML) : ''
}

// Flipper flags are exposed to JS as hidden divs (see index.html.erb)
export function flipperFlagEnabled (dataId) {
  const flagElement = document.querySelector(`[data-id="${dataId}"]`)
  return flagElement ? flagElement.innerText.trim() === 'true' : false
}

function setFiltersFromVibe (helper) {
  vibes.VIBES.forEach(value => {
    if (window.location.pathname === ('/' + stringParameterize(value))) {
      helper.addDisjunctiveFacetRefinement('vibe_names', value)
    }
  })
}

function setFiltersFromSellerSlug (helper) {
  if (window.location.pathname.includes('/sellers/')) {
    helper.addDisjunctiveFacetRefinement('user_slug', sellerSlugForSearch())
  }
}

// The hidden storefront URL uses a non-identifying storefront_slug that isn't
// the listings' user_slug facet, so read the seller's real slug rendered on the
// page. Falls back to the URL segment for the public /sellers/:slug page.
function sellerSlugForSearch () {
  const element = document.querySelector('[data-id="content-seller-slug"]')
  if (element && element.textContent.trim()) {
    return element.textContent.trim()
  }
  const path = removeTraillingSlash(window.location.pathname)
  return path.substring(path.lastIndexOf('/') + 1)
}

function setFiltersForSale (helper) {
  if (window.location.pathname.includes('/sale')) {
    helper.addFacetRefinement('sale', true)
  }
}

function setFiltersForNewIn (helper) {
  if (window.location.pathname.includes('/new')) {
    helper.addFacetRefinement('published_recently', true)
  }
}

function setFilterForCommerciallyAccessible (helper) {
  const params = new URLSearchParams(window.location.search)
  const commercialParam = params.get('commercially_accessible')

  if (commercialParam !== null) {
    helper.addFacetRefinement('commercially_accessible', commercialParam === 'true')
  }
}

function setFilterForLuxuryItems (helper) {
  const params = new URLSearchParams(window.location.search)
  const luxuryParam = params.get('luxury_items')

  if (luxuryParam !== null) {
    helper.addFacetRefinement('luxury_items', luxuryParam === 'true')
  }
}

function removeTraillingSlash (pathname) {
  if (pathname.slice(-1) === '/') {
    return pathname.slice(0, -1)
  } else {
    return pathname
  }
}

export function addCollapseOnClick () {
  const container = document.getElementById('vjs-filters-container')
  if (!container || container.dataset.collapseListenerAdded) return

  container.dataset.collapseListenerAdded = 'true'

  container.addEventListener('click', (event) => {
    const header = event.target.closest('.ais-Panel-header')
    const panel = header?.closest('.ais-Panel')
    if (!panel) return

    event.stopPropagation()
    panel.classList.toggle('ais-Panel--collapsed')
  }, true) // true = capture phase (opposite of bubbling)
  // Using capture phase ensures we fire before Algolia's native handler, to prevent Algolia from collapsing the panel after our click to open it.
}

function cleanVibes (vibes) {
  return vibes.filter(vibe => !(vibe === 'Unknown' || vibe === ''))
}

export function buildFilters (filtersConfig) {
  return typesenseEnabled() ? buildTypesenseFilters(filtersConfig) : buildAlgoliaFilters(filtersConfig)
}

const escapeTypesenseValue = escapeValue

// `:=` exact match, required on string fields (`:` is a token match).
// Unconditional, unlike filterSyntax#exactMatch — the engine is known here.
function typesenseExactMatch (field, value) {
  return `${field}:=${escapeTypesenseValue(value)}`
}

function buildTypesenseFilters (filtersConfig) {
  const {
    listingId, moreFromThisSeller, userSlug, productType, subProductType, subcategory, crossCategory, crossCategoryLvl0Names, vibeNames, listingIds
  } = filtersConfig

  if (listingIds) {
    // `id:[...]` is the IN operator; objectID is not a Typesense field.
    const listingIdsArray = JSON.parse(listingIds)

    return `sold:false && id:[${listingIdsArray.join(',')}]`
  }

  const baseFilter = `sold:false && id:!=${listingId}`

  if (moreFromThisSeller) {
    return `${baseFilter} && ${typesenseExactMatch('user_slug', userSlug)}`
  }

  if (crossCategory) {
    const lvl0Names = crossCategoryLvl0Names ? JSON.parse(crossCategoryLvl0Names) : ['Home Decor', 'Lighting']
    const lvl0String = lvl0Names.map(escapeTypesenseValue).join(',')
    const vibes = cleanVibes(vibeNames.split(','))
    const crossCategoryFilter = `${baseFilter} && category_hierarchy.lvl0:=[${lvl0String}]`
    if (vibes.length > 0) {
      return `${crossCategoryFilter} && vibe_names:=[${vibes.map(escapeTypesenseValue).join(',')}]`
    } else {
      return crossCategoryFilter
    }
  }

  if (subProductType) {
    return `${baseFilter} && ${typesenseExactMatch('sub_product_type_name', subProductType)}`
  }

  if (productType) {
    return `${baseFilter} && ${typesenseExactMatch('product_type_name', productType)}`
  } else {
    return `${baseFilter} && ${typesenseExactMatch('subcategory_name', subcategory)}`
  }
}

function buildAlgoliaFilters (filtersConfig) {
  const {
    listingId, moreFromThisSeller, userSlug, productType, subProductType, subcategory, crossCategory, crossCategoryLvl0Names, vibeNames, listingIds
  } = filtersConfig

  if (listingIds) {
    const listingIdsArray = JSON.parse(listingIds)
    const objectIdsString = listingIdsArray.map((objectId) => `objectID:${objectId}`).join(' OR ')

    return `sold:false AND (${objectIdsString})`
  }

  const baseFilter = 'sold:false AND NOT objectID:' + listingId

  // TODO: Can't we just test for the existence of userSlug?
  if (moreFromThisSeller) {
    return baseFilter + ' AND user_slug:"' + userSlug + '"'
  }

  if (crossCategory) {
    const lvl0Names = crossCategoryLvl0Names ? JSON.parse(crossCategoryLvl0Names) : ['Home Decor', 'Lighting']
    const lvl0String = lvl0Names.map((name) => `category_hierarchy.lvl0:"${name}"`).join(' OR ')
    const vibes = cleanVibes(vibeNames.split(','))
    if (vibes.length > 0) {
      const vibeString = vibes.map((vibe) => `vibe_names:"${vibe}"`).join(' OR ')
      return baseFilter + ` AND (${lvl0String}) AND (${vibeString})`
    } else {
      return baseFilter + ` AND (${lvl0String})`
    }
  }

  if (subProductType) {
    return baseFilter + ' AND sub_product_type_name:"' + subProductType + '"'
  }

  if (productType) {
    return baseFilter + ' AND product_type_name:"' + productType + '"'
  } else {
    return baseFilter + ' AND subcategory_name:"' + subcategory + '"'
  }
}

export function initiateSearch (filters = 'sold:false', explicitIndex = null) {
  const index = getSearchIndex(explicitIndex, adapterOverrides({ seekrSortBy: getSeekrSortBy() }))
  const hardCodedHitsPerPage = 20
  const isTracked = isClickAnalyticsEnabled()
  return index.search('', {
    hitsPerPage: hardCodedHitsPerPage,
    page: 0,
    filters: filters,
    clickAnalytics: isTracked,
    optionalFilters: getOptionalFilters(),
    analytics: isTracked,
    attributesToRetrieve: [
      '*',
      '-description'
    ]
  })
}

export function initiateSimilarSearch (similarQuery, hitsPerPage, filters) {
  const isTracked = isClickAnalyticsEnabled()
  const onTypesense = typesenseEnabled()
  const index = getSearchIndex(
    null,
    adapterOverrides({ similarQuery: true, seekrSortBy: getSeekrSortBy() })
  )

  return index.search(onTypesense ? similarQuery : '', {
    hitsPerPage: hitsPerPage,
    similarQuery: similarQuery,
    filters: filters,
    optionalFilters: getOptionalFilters(),
    clickAnalytics: isTracked,
    attributesToRetrieve: [
      '*',
      '-description'
    ]
  }).then(results => decorateHitsForInsights(results))
}

export function initiateKeywordSearch (query, hitsPerPage, filters) {
  // neverBroaden is the Typesense form of `removeWordsIfNoResults: 'none'`
  // below; it has to be baked into the adapter instance.
  const index = getSearchIndex(
    null,
    adapterOverrides({ neverBroaden: true, seekrSortBy: getSeekrSortBy() })
  )
  const isTracked = isClickAnalyticsEnabled()

  return index.search(query, {
    hitsPerPage: hitsPerPage,
    filters: filters,
    removeWordsIfNoResults: 'none',
    optionalFilters: getOptionalFilters(),
    clickAnalytics: isTracked,
    analytics: isTracked,
    attributesToRetrieve: [
      '*',
      '-description'
    ]
  }).then(results => decorateHitsForInsights(results))
}

// Click-attribution metadata Algolia insights expects on every hit
// (idempotent — some callers still set it themselves).
function decorateHitsForInsights (results) {
  const queryID = results.queryID || ''
  results.hits.forEach((hit, index) => {
    hit.__position = index + 1
    hit.__queryID = queryID
  })
  return results
}

// Algolia's `Index#search(query, params)` over whichever engine is running.
// `overrides` (build with adapterOverrides) carry what cannot ride on a
// request. On Typesense a named `index` resolves to the page locale's
// collection — deliberate: the one caller passing a name hardcoded the
// English index on translated pages.
export function getSearchIndex (index = null, overrides = {}) {
  if (typesenseEnabled()) {
    return typesenseSearchIndex(primaryIndexName(index || getAlgoliaIndexFromUrl()), overrides)
  }

  const client = algoliasearch(process.env.ALGOLIASEARCH_APPLICATION_ID, process.env.ALGOLIASEARCH_API_KEY_SEARCH, { protocol: 'https:' })
  if (index) {
    return client.initIndex(index)
  } else {
    return client.initIndex(getAlgoliaIndexFromUrl())
  }
}

export function isCollectionPage () {
  const collectionsUrlRegex = /collections\/.*$/
  return collectionsUrlRegex.test(window.location.pathname)
}

// Collection pages search on the collection name, and the flag narrows that
// to titles only. Read at adapter-build time on Typesense (see
// restrictToTitle in adapterOverrides), so it lives on its own.
export function shouldRestrictCollectionTitle () {
  return isCollectionPage() && getTable('restrict-collection-title') === true
}

export function setCollectionsQueryFromUrl (helper) {
  if (isCollectionPage()) {
    const splitPath = window.location.pathname.split('/')
    const indexOfCollectionQuery = splitPath.indexOf('collections') + 1
    const collection = splitPath[indexOfCollectionQuery].replace(/[_-]/g, ' ')
    // Algolia only: restrictSearchableAttributes cannot travel on a Typesense
    // request (the adapter drops it), so there the narrowing is a query_by
    // override baked into the adapter instead.
    if (shouldRestrictCollectionTitle() && !typesenseEnabled()) {
      helper.setQueryParameter('restrictSearchableAttributes', ['title'])
    }
    helper.setQuery(collection)
  }
}

// Likely a combination of these comments will be used soon
// here or on filters(): like we do for showcases, depends on how complex our AND /OR logic is
export function setRoomTypeQueryFromUrl (helper) {
  if (urlHelpers.isRoomTypePage()) {
    const splitPath = window.location.pathname.split('/')
    const indexOfRoomTypeQuery = splitPath.indexOf('room-type') + 1
    const roomType = splitPath[indexOfRoomTypeQuery]

    // These work like filters, conjunctive
    // args.helper.addTag("jan-sale-carryover")
    // args.helper.addDisjunctiveFacetRefinement('_tags', "jan-sale-carryover")
    // Some other price custom field

    const roomTypeCategories = getTable('room-type-categories')[roomType]
    if (roomTypeCategories) {
      roomTypeCategories.forEach(category => {
        helper.addDisjunctiveFacetRefinement('most_specific_category_name', category)
      })
    }
  }
}

export function getOptionalFilters () {
  const optionalFilters = getTable('seekr-optional-filters')
  return optionalFilters
}

// The viewer's affinities as a Typesense sort_by, rendered by
// SeekrOptionalFilters#seekr_sort_by_for_js.
export function getSeekrSortBy () {
  const element = document.querySelector('[data-id="content-seekr-sort-by"]')
  return element ? element.textContent.trim() : ''
}

export function getTemplate (templateName) {
  return document.getElementById(templateName + '-template').innerHTML
}

export function getAlgoliaAllocation () {
  const element = document.querySelector('[data-id="content-algolia-experiment-allocation"]')
  return (element !== null && element.innerHTML !== '') ? element.innerHTML : 'control'
}

// The default locale (mirrors Ruby Locale.default.code). Default-locale index
// names carry no locale suffix.
const DEFAULT_LOCALE = 'en'

// Mirrors Ruby Locale.index_name: default locale -> `Listing_<env>`, otherwise
// the underscore form of the code, e.g. 'nl-nl' -> `Listing_nl_nl_<env>`.
export function localeListingIndex (locale, nodeEnv) {
  if (!locale || locale === DEFAULT_LOCALE) return `Listing_${nodeEnv}`
  return `Listing_${locale.replace(/-/g, '_')}_${nodeEnv}`
}

export function getAlgoliaIndexFromUrl () {
  const nodeEnv = process.env.NODE_ENV
  const locale = getLocaleFromPath(window.location.pathname)

  if (locale && locale !== DEFAULT_LOCALE) {
    return localeListingIndex(locale, nodeEnv)
  }

  if (urlHelpers.inExperiment('vSearchAlgoVar')) {
    return `Listing_${nodeEnv}_${urlHelpers.getExperimentVariant('vSearchAlgoVar')}`
  } else {
    return `Listing_${nodeEnv}`
  }
}

export function getAlgoliaSponsoredIndex () {
  const nodeEnv = process.env.NODE_ENV
  const locale = getLocaleFromPath(window.location.pathname)

  if (locale && locale !== DEFAULT_LOCALE) {
    return `${localeListingIndex(locale, nodeEnv)}_sponsored`
  }

  return `Listing_${nodeEnv}_sponsored`
}

// The five sort orders, keyed by engine-neutral intent. The sort dropdown
// and the legacy-URL translator both resolve through this one table.
const SORT_ORDER = ['relevance', 'price_asc', 'sale_percentage_desc', 'price_desc', 'published_at_desc']

const SORT_LABELS = {
  relevance: 'most_relevant',
  price_asc: 'lowest_price',
  sale_percentage_desc: 'highest_percentage_off',
  price_desc: 'highest_price',
  published_at_desc: 'newest_first'
}

export function getIndicesForSortBySelector (indexName) {
  const values = sortValuesByIntent(indexName)

  return SORT_ORDER.map((intent) => ({ value: values[intent], label: t(SORT_LABELS[intent]) }))
}

function sortValuesByIntent (indexName) {
  return typesenseEnabled() ? typesenseSortValues(indexName) : algoliaSortValues(indexName)
}

function algoliaSortValues (indexName) {
  const priceType = isTradeUser() ? '_trade' : ''

  return {
    relevance: indexName,
    price_asc: `${indexName}${priceType}_price_asc`,
    sale_percentage_desc: `${indexName}_sale_percentage_desc`,
    price_desc: `${indexName}${priceType}_price_desc`,
    published_at_desc: `${indexName}_published_at_desc`
  }
}

// Algolia's per-sort replica indexes collapse into `/sort/` strings against
// one collection. Trade buyers sort on their own price field.
function typesenseSortValues (collection) {
  const priceField = getPriceAttributeName()

  // The adapter derives sort_by from the index name and overrides
  // QueryBuilder's, so the tiebreaker must ride along here or equal-priced
  // listings reshuffle between pages. Follows the forced ranking variant.
  const TIEBREAKER = `${rankingField()}:desc`
  const sorted = (field, direction) => `${collection}/sort/${field}:${direction},${TIEBREAKER}`

  return {
    // No `/sort/` suffix: falls through to the relevance ranking.
    relevance: collection,
    price_asc: sorted(priceField, 'asc'),
    sale_percentage_desc: sorted('sale_percentage_int', 'desc'),
    price_desc: sorted(priceField, 'desc'),
    published_at_desc: sorted('published_at_int', 'desc')
  }
}

// Legacy `sortBy` in URLs: sortBy travels as an index name, and every
// Algolia-era URL, bookmark and sent email carries a replica name the other
// engine 404s on (the adapter throws, the grid renders nothing). Translation
// happens at read time, bidirectionally — the experiment means either engine
// can receive the other's URL. Default-deny: anything unrecognised becomes
// relevance; passing an unknown index name through is precisely the bug.
// See docs/search/typesense-migration.md.
const LEGACY_SORT_SUFFIXES = [
  // Order matters; the price entries catch the dead replica families
  // (_trade_, _plus_delivery_, _sale_, _new_) by suffix alone.
  ['_published_at_desc', 'published_at_desc'],
  ['_sale_percentage_desc', 'sale_percentage_desc'],
  ['_price_asc', 'price_asc'],
  ['_price_desc', 'price_desc']
]

function sortIntentFromValue (sortBy) {
  const value = String(sortBy)

  // Typesense form: `<collection>/sort/<field>:<direction>,<tiebreaker>`
  const sortSegment = value.split('/sort/')[1]
  if (sortSegment) {
    const [field, direction] = sortSegment.split(',')[0].split(':')
    if (field === 'published_at_int') return 'published_at_desc'
    if (field === 'sale_percentage_int') return 'sale_percentage_desc'
    if (field.startsWith('display_price')) return direction === 'desc' ? 'price_desc' : 'price_asc'
    return null
  }

  const suffix = LEGACY_SORT_SUFFIXES.find(([legacy]) => value.endsWith(legacy))
  if (suffix) return suffix[1]

  // A bare index name (`Listing_production`, `Listing_nl_nl_production`) is the
  // Algolia primary, i.e. relevance.
  if (/^Listing_/.test(value)) return 'relevance'

  return null
}

export function legacySortByToEngineValue (sortBy, indexName) {
  if (!sortBy) return undefined

  const values = sortValuesByIntent(indexName)
  if (Object.values(values).includes(sortBy)) return sortBy

  const intent = sortIntentFromValue(sortBy)
  return intent ? values[intent] : undefined
}

// The viewer's chosen sort travels in the InstantSearch index name, never in
// the Typesense response — the adapter emits no `index` key, so `results.index`
// is undefined on every client render.
export function hardSortActive (renderOpts) {
  return String(renderOpts?.results?._state?.index || '').includes('/sort/')
}

function isTradeUser () {
  return document.querySelector('[data-trade-buyer]')?.dataset?.tradeBuyer === 'true'
}

export function getPriceAttributeName () {
  return `display_price${isTradeUser() ? '_trade_user' : ''}_for_algolia`
}

export function setFiltersFromUrl (helper) {
  setFiltersFromVibe(helper)
  setFiltersFromSellerSlug(helper)
  setFiltersForSale(helper)
  setFiltersForNewIn(helper)
  setFilterForCommerciallyAccessible(helper)
  setFilterForLuxuryItems(helper)
}

export function pushAlgoliaAllocationPDP (indexUsed) {
  if (!haveGtmAndGaLoaded()) return
  if (mainIndex(indexUsed)) {
    const expVersion = indexUsed === 'Listing_production' ? 'control' : 'variant'
    window.dataLayer.push({
      event: 'ce - amplitude - setAlgoliaAllocations',
      user: {
        enabled_flipper_features: {
          algolia_main_ab: expVersion
        }
      }
    })
  }
}

// This method of sending allocation to Amplitude is being deprecated
// See user.algolia_allocations
export function setPLPUsedIndex (renderOpts) {
  if (renderOpts.results._rawResults[0] !== undefined) {
    const { indexUsed, abTestVariantID } = renderOpts.results._rawResults[0]
    // Algolia A/B-testing fields with no Typesense equivalent; without the
    // guard a GTM event claims an allocation that does not exist.
    if (indexUsed === undefined) return
    const hitsContainer = document.getElementById('hits')
    hitsContainer.setAttribute('data-index-used', indexUsed)

    if (!haveGtmAndGaLoaded()) return
    window.dataLayer.push({
      page: {
        search_index_used: indexUsed
      }
    })

    if (abTestVariantID) {
      const expVersion = abTestVariantID === 1 ? 'control' : 'variant'
      window.dataLayer.push({
        event: 'ce - amplitude - setAlgoliaAllocations',
        user: {
          enabled_flipper_features: {
            algolia_generic_ab: expVersion
          }
        }
      })
    }

    if (mainIndex(indexUsed)) {
      const expVersion = indexUsed === 'Listing_production' ? 'control' : 'variant'
      window.dataLayer.push({
        event: 'ce - amplitude - setAlgoliaAllocations',
        user: {
          enabled_flipper_features: {
            algolia_main_ab: expVersion
          }
        }
      })
    }
  }
}

function mainIndex (indexUsed) {
  return indexUsed === 'Listing_production' || indexUsed === 'Listing_production_var_1'
}

export function isBot () {
  const botOrRendertron = document.querySelector('.vjs-bot-or-rendertron')
  return JSON.parse(botOrRendertron?.value)
}

export function isClickAnalyticsEnabled () {
  return !isBot()
}

function openDB () {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB is not supported by this browser.'))
      return
    }

    const request = indexedDB.open('AlgoliaAnalyticsDB', 3)

    request.onerror = function (event) {
      reject(new Error('IndexedDB error: ' + event.target.errorCode))
    }

    request.onsuccess = function (event) {
      const db = event.target.result

      // Perform cleanup of old records on database open
      const transaction = db.transaction(['queryids', 'queryindexes', 'searchorigins'], 'readwrite')
      const stores = ['queryids', 'queryindexes', 'searchorigins']

      stores.forEach((storeName) => {
        const store = transaction.objectStore(storeName)
        if (store.indexNames.contains('timestamp')) {
          const index = store.index('timestamp')
          // Double check, but think the attribution window for a queryID in Algolia is 7 days
          const twoWeeksAgo = Date.now() - 14 * 24 * 60 * 60 * 1000 // 2 weeks in milliseconds
          const range = IDBKeyRange.upperBound(twoWeeksAgo)

          index.openCursor(range).onsuccess = function (event) {
            const cursor = event.target.result
            if (cursor) {
              const record = cursor.value
              if (record.timestamp) {
                store.delete(cursor.primaryKey)
              }
              cursor.continue()
            }
          }
        }
      })

      resolve(db)
    }

    request.onupgradeneeded = function (event) {
      const db = event.target.result

      const createStore = (storeName) => {
        if (!db.objectStoreNames.contains(storeName)) {
          const store = db.createObjectStore(storeName, { keyPath: 'listingID' })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        } else {
          const transaction = event.currentTarget.transaction
          const store = transaction.objectStore(storeName)
          if (!store.indexNames.contains('timestamp')) {
            store.createIndex('timestamp', 'timestamp', { unique: false })
          }
        }
      }

      createStore('queryids')
      createStore('queryindexes')
      createStore('searchorigins')
    }
  })
}

export async function setQueryID (listingID, queryID) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['queryids'], 'readwrite')
    const store = transaction.objectStore('queryids')
    const timestamp = Date.now()
    store.put({ listingID, queryID, timestamp })
  } catch (error) {
    console.error('Falling back to localStorage due to:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      window.localStorage.setItem(`algoliaQueryID-${listingID}`, queryID)
    }
  }
}

export async function getQueryID (listingID) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['queryids'], 'readonly')
    const store = transaction.objectStore('queryids')
    const request = store.get(listingID)

    return new Promise((resolve, reject) => {
      request.onsuccess = function (event) {
        if (event.target.result) {
          resolve(event.target.result.queryID)
        } else {
          if (window.localStorage) {
            resolve(window.localStorage.getItem(`algoliaQueryID-${listingID}`))
          } else {
            resolve(null)
          }
        }
      }
      request.onerror = function (event) {
        reject(new Error('IndexedDB read error: ' + event.target.errorCode))
      }
    })
  } catch (error) {
    console.error('Error accessing IndexedDB:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      return window.localStorage.getItem(`algoliaQueryID-${listingID}`)
    }
    return null
  }
}

export async function setQueryIndex (listingID, indexUsed) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['queryindexes'], 'readwrite')
    const store = transaction.objectStore('queryindexes')
    const timestamp = Date.now()
    store.put({ listingID, indexUsed, timestamp })
  } catch (error) {
    console.error('Falling back to localStorage due to:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      window.localStorage.setItem(`algoliaQueryIndex-${listingID}`, indexUsed)
    }
  }
}

export async function getQueryIndex (listingID) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['queryindexes'], 'readonly')
    const store = transaction.objectStore('queryindexes')
    const request = store.get(listingID)

    return new Promise((resolve, reject) => {
      request.onsuccess = function (event) {
        if (event.target.result) {
          resolve(event.target.result.indexUsed)
        } else {
          if (window.localStorage) {
            resolve(window.localStorage.getItem(`algoliaQueryIndex-${listingID}`))
          } else {
            resolve('Listing_production')
          }
        }
      }
      request.onerror = function (event) {
        reject(new Error('IndexedDB read error: ' + event.target.errorCode))
      }
    })
  } catch (error) {
    console.error('Error accessing IndexedDB:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      return window.localStorage.getItem(`algoliaQueryIndex-${listingID}`)
    }
    return 'Listing_production'
  }
}

export async function setSearchOrigin (listingID, searchOrigin) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['searchorigins'], 'readwrite')
    const store = transaction.objectStore('searchorigins')
    const timestamp = Date.now()
    store.put({ listingID, searchOrigin, timestamp })
  } catch (error) {
    console.error('Falling back to localStorage due to:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      window.localStorage.setItem(`algoliaSearchOrigin-${listingID}`, searchOrigin)
    }
  }
}

export async function getSearchOrigin (listingID) {
  try {
    const db = await openDB()
    const transaction = db.transaction(['searchorigins'], 'readonly')
    const store = transaction.objectStore('searchorigins')
    const request = store.get(listingID)

    return new Promise((resolve, reject) => {
      request.onsuccess = function (event) {
        if (event.target.result) {
          resolve(event.target.result.searchOrigin)
        } else {
          if (window.localStorage) {
            resolve(window.localStorage.getItem(`algoliaSearchOrigin-${listingID}`))
          } else {
            resolve(null)
          }
        }
      }
      request.onerror = function (event) {
        reject(new Error('IndexedDB read error: ' + event.target.errorCode))
      }
    })
  } catch (error) {
    console.error('Error accessing IndexedDB:', error)
    Sentry.captureException(error)
    if (window.localStorage) {
      return window.localStorage.getItem(`algoliaSearchOrigin-${listingID}`)
    }
    return null
  }
}
